// ── 로또 등수 계산 공용 모듈 ─────────────────────────────────────────────
// 내 번호 페이지와 당첨 시뮬레이터가 동일한 등수 판정 로직을 공유한다.

export interface RankResult {
  rank: number | null;
  matchedCount: number;
  bonusMatched: boolean;
}

/**
 * 1~45 사이 서로 다른 6개 정수 배열인지 검사한다.
 * 잘못된 입력을 성공 등수로 판정하지 않기 위한 전제 검증.
 */
function isSixUnique(nums: unknown): nums is number[] {
  if (!Array.isArray(nums) || nums.length !== 6) return false;
  const seen = new Set<number>();
  for (const n of nums) {
    if (typeof n !== 'number' || !Number.isInteger(n) || n < 1 || n > 45) return false;
    if (seen.has(n)) return false;
    seen.add(n);
  }
  return true;
}

/**
 * 내 번호와 당첨 번호를 비교해 등수를 판정한다.
 * - 6개 일치 → 1등
 * - 5개 + 보너스 일치 → 2등
 * - 5개, 보너스 불일치 → 3등
 * - 4개 → 4등 / 3개 → 5등 / 0~2개 → 낙첨(rank null)
 * - 보너스 번호가 내 번호에 포함돼도 본 번호 일치 개수와 혼동하지 않음(2등은 5개 일치 시에만)
 * - 잘못된 입력(6개 고유 정수 1~45가 아님)은 성공 등수로 판정하지 않고 낙첨(null)으로 처리
 * - 입력 배열을 변경하지 않음
 */
export function checkRank(
  myNumbers: readonly number[],
  winningNumbers: readonly number[],
  bonus: number,
): RankResult {
  if (
    !isSixUnique(myNumbers) ||
    !isSixUnique(winningNumbers) ||
    typeof bonus !== 'number' ||
    !Number.isInteger(bonus) ||
    bonus < 1 ||
    bonus > 45
  ) {
    return { rank: null, matchedCount: 0, bonusMatched: false };
  }

  const winningSet = new Set(winningNumbers);
  // 보너스가 당첨 번호에 포함된 잘못된 추첨 데이터는 성공 등수로 판정하지 않는다.
  if (winningSet.has(bonus)) {
    return { rank: null, matchedCount: 0, bonusMatched: false };
  }

  let matchedCount = 0;
  for (const n of myNumbers) {
    if (winningSet.has(n)) matchedCount += 1;
  }
  const bonusMatched = myNumbers.includes(bonus);

  if (matchedCount === 6) return { rank: 1, matchedCount, bonusMatched };
  if (matchedCount === 5 && bonusMatched) return { rank: 2, matchedCount, bonusMatched };
  if (matchedCount === 5) return { rank: 3, matchedCount, bonusMatched };
  if (matchedCount === 4) return { rank: 4, matchedCount, bonusMatched };
  if (matchedCount === 3) return { rank: 5, matchedCount, bonusMatched };
  return { rank: null, matchedCount, bonusMatched };
}